import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  LayoutDashboard, Calendar, Users, ArrowLeftRight,
  Bell, LogOut, Footprints, Upload, UserCheck, Printer, Settings, MapPin, Network, ShieldCheck, Menu, X, Crown, Lock, Handshake, DollarSign, Landmark, Feather, GraduationCap, Heart, HeartHandshake, Megaphone, UsersRound, ChevronDown, ChevronRight, ExternalLink, Briefcase, FileText, Zap, ClipboardList, BarChart3
} from 'lucide-react';

const THERAP_URL = 'https://secure.therapservices.net/auth/login?successUrl=https%3A%2F%2Fsecure.therapservices.net%3A443%2Fma%2Fhome%2Ft%2F';
const ADP_URL = 'https://online.adp.com/signin/v1/?APPID=wfncloud&productId=80e309c3-70ca-bae1-e053-3505430b5495&returnURL=https://workforcenow.cloud.adp.com/&callingAppId=wfncloud&TARGET=-SM-https://workforcenow.cloud.adp.com/theme/index.html%23/home';

// Pinned links every employee sees at the top of the sidebar, in this exact order.
const PINNED_TOP = [
  { to: '/my-schedule', icon: Footprints,   label: 'My Schedule' },
  { to: '/my-reviews',  icon: ClipboardList, label: 'My Reviews' },
  { href: ADP_URL,      icon: Briefcase,    label: 'ADP',    external: true, pinnedTop: true },
  { href: THERAP_URL,   icon: FileText,     label: 'Therap', external: true, pinnedTop: true },
];

const ROLE_LABELS = {
  administrator: 'Administrator',
  supervisor: 'Supervisor',
  scheduler: 'Scheduler',
  staff: 'Staff',
  board_director: 'Board Director',
  hr: 'Human Resources',
};

const HR_GROUP = {
  label: 'HR',
  icon: UsersRound,
  isGroup: true,
  children: [
    { to: '/onboarding', icon: GraduationCap, label: 'Orientation & Training', hrModule: 'orientation' },
    { to: '/employees',  icon: Users,         label: 'Staff',                 hrModule: 'staff' },
    { to: '/credentials', icon: ShieldCheck,  label: 'Credentials',           hrModule: 'credentials' },
    { to: '/staffing',   icon: Network,       label: 'Staffing',              hrModule: 'staffing' },
    { to: '/performance', icon: BarChart3,    label: 'Performance',           perfGated: true },
  ],
};

const adminLinks = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/schedule', icon: Calendar, label: 'Schedule' },
  { to: '/my-schedule', icon: Footprints, label: 'My Schedule' },
  { to: '/activities', icon: MapPin, label: 'Activities' },
  { to: '/meetings', icon: Handshake, label: 'Meetings' },
  { to: '/participants', icon: UserCheck, label: 'Participants' },
  { to: '/caseloads', icon: Network, label: 'Caseloads' },
  { to: '/finance', icon: DollarSign, label: 'Finance', financeGated: true },
  { to: '/board', icon: Landmark, label: 'Board Portal', boardGated: true },
  { to: '/print', icon: Printer, label: 'Print / Export' },
  { to: '/shift-requests', icon: ArrowLeftRight, label: 'Requests' },
  { to: '/my-reviews', icon: ClipboardList, label: 'My Reviews' },
  { to: '/notifications', icon: Bell, label: 'Notifications' },
  HR_GROUP,
  { to: '/volunteers', icon: HeartHandshake, label: 'Volunteers' },
  { to: '/donors', icon: Heart, label: 'Donors', donorGated: true },
  { to: '/marketing', icon: Megaphone, label: 'Marketing', marketingGated: true },
  { to: '/policies', icon: ShieldCheck, label: 'Policies' },
  { to: '/settings', icon: Settings, label: 'Settings' },
];

const supervisorLinks = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/schedule', icon: Calendar, label: 'Schedule' },
  { to: '/my-schedule', icon: Footprints, label: 'My Schedule' },
  { to: '/activities', icon: MapPin, label: 'Activities' },
  { to: '/meetings', icon: Handshake, label: 'Meetings' },
  { to: '/participants', icon: UserCheck, label: 'Participants' },
  { to: '/caseloads', icon: Network, label: 'Caseloads' },
  { to: '/finance', icon: DollarSign, label: 'Finance', financeGated: true },
  { to: '/board', icon: Landmark, label: 'Board Portal', boardGated: true },
  { to: '/print', icon: Printer, label: 'Print / Export' },
  { to: '/shift-requests', icon: ArrowLeftRight, label: 'Requests' },
  { to: '/my-reviews', icon: ClipboardList, label: 'My Reviews' },
  { to: '/notifications', icon: Bell, label: 'Notifications' },
  HR_GROUP,
  { to: '/volunteers', icon: HeartHandshake, label: 'Volunteers' },
  { to: '/donors', icon: Heart, label: 'Donors', donorGated: true },
  { to: '/marketing', icon: Megaphone, label: 'Marketing', marketingGated: true },
  { to: '/policies', icon: ShieldCheck, label: 'Policies' },
];

const schedulerLinks = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/schedule', icon: Calendar, label: 'Schedule' },
  { to: '/my-schedule', icon: Footprints, label: 'My Schedule' },
  { to: '/activities', icon: MapPin, label: 'Activities' },
  { to: '/meetings', icon: Handshake, label: 'Meetings' },
  { to: '/print', icon: Printer, label: 'Print / Export' },
  { to: '/shift-requests', icon: ArrowLeftRight, label: 'Requests' },
  { to: '/my-reviews', icon: ClipboardList, label: 'My Reviews' },
  { to: '/notifications', icon: Bell, label: 'Notifications' },
  { to: '/donors', icon: Heart, label: 'Donors', donorGated: true },
  { to: '/marketing', icon: Megaphone, label: 'Marketing', marketingGated: true },
  { to: '/policies', icon: ShieldCheck, label: 'Policies' },
];

const staffLinks = [
  { to: '/my-schedule', icon: Calendar, label: 'My Schedule', moduleKey: 'my_schedule' },
  { to: '/activities', icon: MapPin, label: 'Activities', moduleKey: 'activities' },
  { to: '/meetings', icon: Handshake, label: 'Meetings', moduleKey: 'meetings' },
  { to: '/shift-requests', icon: ArrowLeftRight, label: 'Requests', moduleKey: 'requests' },
  { to: '/my-reviews', icon: ClipboardList, label: 'My Reviews' },
  { to: '/notifications', icon: Bell, label: 'Notifications', moduleKey: 'notifications' },
  { to: '/onboarding', icon: GraduationCap, label: 'Orientation & Training', moduleKey: 'onboarding' },
  { to: '/donors', icon: Heart, label: 'Donors', donorGated: true },
  { to: '/marketing', icon: Megaphone, label: 'Marketing', marketingGated: true },
  { to: '/policies', icon: ShieldCheck, label: 'Policies', moduleKey: 'policies' },
];

// Board directors are siloed: no operational pages, only the board portal
// (plus notifications + policies so they can stay informed).
const boardDirectorLinks = [
  { to: '/board', icon: Landmark, label: 'Board Portal' },
  { to: '/notifications', icon: Bell, label: 'Notifications' },
  { to: '/policies', icon: ShieldCheck, label: 'Policies' },
];

// HR people are siloed to the onboarding lifecycle. They review attestations
// and pull progress/CSV records — they don't see scheduling, finance, board,
// or the staff roster page.
const hrLinks = [
  { to: '/onboarding', icon: GraduationCap, label: 'Orientation & Training' },
  { to: '/notifications', icon: Bell, label: 'Notifications' },
  { to: '/policies', icon: ShieldCheck, label: 'Policies' },
];

function getLinksForRole(role) {
  switch (role) {
    case 'administrator': return adminLinks;
    case 'supervisor': return supervisorLinks;
    case 'scheduler': return schedulerLinks;
    case 'board_director': return boardDirectorLinks;
    case 'hr': return hrLinks;
    default: return staffLinks;
  }
}

// Compose final sidebar order:
//   [My Schedule, ADP, Therap]  →  then the rest of the role's links alphabetized by label.
// Board directors are external volunteers, not employees, so they don't get the top-pinned trio.
function composeSidebar(role, roleLinks) {
  if (role === 'board_director') return roleLinks;
  // Strip any pre-existing My Schedule / My Reviews entries (re-pinned at the top).
  const rest = roleLinks.filter(l => l && l.to !== '/my-schedule' && l.to !== '/my-reviews');
  // Sort alphabetically by label (case-insensitive). Groups sort by their `label`
  // (e.g., 'HR') just like any other item.
  const sortedRest = [...rest].sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  return [...PINNED_TOP, ...sortedRest];
}

// Filter links based on user.module_access. Admins/supervisors/schedulers
// always see everything they're entitled to by role; only staff/employee
// see fewer items when modules are turned off for them.
function hasHrAccess(user, hrModule) {
  const role = (user?.role || '').toLowerCase();
  if (role === 'administrator' || role === 'supervisor') return true;
  if (role === 'hr' && (hrModule === 'orientation' || hrModule === 'staff' || hrModule === 'credentials')) return true;
  return !!user?.[`hr_${hrModule}_access`];
}

function filterHrGroup(group, user) {
  const visible = group.children.filter(c => c.perfGated
    ? (user?.email === 'josh@tnsprograms.org' || user?.perf_review_access)
    : hasHrAccess(user, c.hrModule));
  if (visible.length === 0) return null;
  return { ...group, children: visible };
}

function applyModuleAccess(links, user) {
  const role = user?.role;
  const hideDonor = (l) => l.donorGated && !user?.donor_crm_access;
  const hideMkt = (l) => l.marketingGated && !user?.marketing_access;
  const process = (arr) => arr
    .map(l => (l.isGroup && l.label === 'HR') ? filterHrGroup(l, user) : l)
    .filter(Boolean);
  if (role === 'administrator') {
    return process(links).filter(l => !hideDonor(l) && !hideMkt(l));
  }
  if (role === 'supervisor') {
    return process(links).filter(l =>
      (!l.financeGated || user?.finance_access) &&
      (!l.boardGated || user?.board_access) &&
      !hideDonor(l) && !hideMkt(l)
    );
  }
  if (role === 'scheduler') return process(links).filter(l => !hideDonor(l) && !hideMkt(l));
  if (role === 'board_director' || role === 'hr') {
    // Siloed roles: append gated modules only when explicitly granted.
    const extras = [];
    if (user?.donor_crm_access) extras.push({ to: '/donors', icon: Heart, label: 'Donors' });
    if (user?.marketing_access) extras.push({ to: '/marketing', icon: Megaphone, label: 'Marketing' });
    return [...links, ...extras];
  }
  const access = user?.module_access || {};
  return links.filter(l =>
    (!l.moduleKey || access[l.moduleKey] !== false) && !hideDonor(l) && !hideMkt(l)
  );
}

export default function Sidebar({ unreadCount = 0 }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const role = user?.role || 'staff';
  const links = applyModuleAccess(composeSidebar(role, getLinksForRole(role)), user);
  const [mobileOpen, setMobileOpen] = useState(false);
  // Auto-open HR dropdown if we're on one of its child routes
  const isOnHrRoute = ['/onboarding', '/employees', '/credentials', '/staffing'].some(p => location.pathname.startsWith(p));
  const [hrOpen, setHrOpen] = useState(isOnHrRoute);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const serviceScope = user?.service_scope || [];
  const scopeLabel = role === 'administrator' ? '' :
    serviceScope.length === 0 || serviceScope.length === 3 ? 'All Services' :
    serviceScope.map(s => ({ day_program: 'CPS', employment: 'Emp', in_home: 'IHCS' }[s] || s)).join(', ');

  // Bottom-bar nav for staff on mobile (most important pages)
  const staffBottomLinks = [
    { to: '/my-schedule', icon: Calendar, label: 'Schedule' },
    { to: '/shift-requests', icon: ArrowLeftRight, label: 'Requests' },
    { to: '/notifications', icon: Bell, label: 'Alerts' },
    { to: '/policies', icon: ShieldCheck, label: 'Policies' },
  ];

  const SidebarBody = (
    <>
      <div className="p-4 border-b border-white/10">
        <div className="flex items-center justify-center bg-white rounded-2xl py-2 shadow-sm">
          <img src="/tns-logo.png" alt="The Next Step Programs" className="h-12 w-auto object-contain" />
        </div>
        <button
          onClick={() => { setMobileOpen(false); window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true })); }}
          className="mt-3 w-full flex items-center justify-between gap-2 px-3 py-1.5 rounded-full border border-white/15 text-xs text-white/50 hover:border-white/35 hover:text-white hover:bg-white/10 transition-colors"
          data-testid="cmdk-trigger"
          title="Search & quick navigate (⌘K)"
        >
          <span className="flex items-center gap-1.5"><svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>Quick search…</span>
          <kbd className="hidden sm:inline-block px-1.5 py-0.5 rounded bg-white/10 text-[10px] font-mono text-white/60 border border-white/15">⌘K</kbd>
        </button>
      </div>

      <nav className="flex-1 p-4 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
        {links.map((item) => {
          if (item.isGroup) {
            const GroupIcon = item.icon;
            const anyChildActive = item.children.some(c => location.pathname.startsWith(c.to));
            return (
              <div key={item.label} data-testid={`sidebar-group-${item.label.toLowerCase()}`}>
                <button
                  onClick={() => setHrOpen(o => !o)}
                  className={`w-full flex items-center gap-3 px-3 py-3 md:py-2.5 rounded-xl text-sm font-medium transition-colors duration-200
                    ${anyChildActive
                      ? 'bg-white/10 text-white font-semibold'
                      : 'text-[#C7BADF] hover:bg-white/10 hover:text-white'
                    }`}
                  data-testid={`sidebar-group-toggle-${item.label.toLowerCase()}`}
                  aria-expanded={hrOpen}
                >
                  <GroupIcon className="h-4.5 w-4.5 shrink-0" />
                  <span className="flex-1 text-left">{item.label}</span>
                  {hrOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
                {hrOpen && (
                  <div className="ml-4 pl-3 border-l border-white/15 space-y-1 mt-1 mb-1" data-testid={`sidebar-group-children-${item.label.toLowerCase()}`}>
                    {item.children.map((child) => {
                      const Icon = child.icon;
                      const testId = `nav-${child.label.toLowerCase().replace(/\s/g, '-')}`;
                      if (child.external) {
                        return (
                          <a
                            key={child.label}
                            href={child.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setMobileOpen(false)}
                            data-testid={testId}
                            title={`Open ${child.label} in a new tab`}
                            className="flex items-center gap-2 px-2.5 py-2 rounded-md text-xs font-medium text-[#C7BADF] hover:bg-white/10 hover:text-white transition-colors"
                          >
                            <Icon className="h-3.5 w-3.5 shrink-0" />
                            <span className="flex-1">{child.label}</span>
                            <ExternalLink className="h-3 w-3 opacity-50" />
                          </a>
                        );
                      }
                      return (
                        <NavLink
                          key={child.to}
                          to={child.to}
                          onClick={() => setMobileOpen(false)}
                          data-testid={testId}
                          className={({ isActive }) =>
                            `flex items-center gap-2 px-2.5 py-2 rounded-md text-xs font-medium transition-colors
                            ${isActive
                              ? 'bg-white text-[#5A2F8E] shadow-sm font-semibold'
                              : 'text-[#C7BADF] hover:bg-white/10 hover:text-white'
                            }`
                          }
                        >
                          <Icon className="h-3.5 w-3.5 shrink-0" />
                          <span>{child.label}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }
          if (item.external) {
            const Icon = item.icon;
            return (
              <a
                key={item.label}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileOpen(false)}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                title={`Open ${item.label} in a new tab`}
                className="flex items-center gap-3 px-3 py-3 md:py-2.5 rounded-xl text-sm font-medium text-[#C7BADF] hover:bg-white/10 hover:text-white transition-colors duration-200"
              >
                <Icon className="h-4.5 w-4.5 shrink-0" />
                <span className="flex-1">{item.label}</span>
                <ExternalLink className="h-3.5 w-3.5 opacity-50" />
              </a>
            );
          }
          const { to, icon: Icon, label } = item;
          return (
            <NavLink
              key={to}
              to={to}
              onClick={() => setMobileOpen(false)}
              data-testid={`nav-${label.toLowerCase().replace(/\s/g, '-')}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-3 md:py-2.5 rounded-xl text-sm font-medium transition-colors duration-200
                ${isActive
                  ? 'bg-white text-[#5A2F8E] shadow-md font-semibold'
                  : 'text-[#C7BADF] hover:bg-white/10 hover:text-white'
                }`
              }
            >
              <Icon className="h-4.5 w-4.5 shrink-0" />
              <span>{label}</span>
              {label === 'Notifications' && unreadCount > 0 && (
                <Badge className="ml-auto bg-[#E07A5F] text-white text-xs h-5 min-w-5 flex items-center justify-center rounded-full">
                  {unreadCount}
                </Badge>
              )}
            </NavLink>
          );
        })}

        {/* Schedule Optimizer (Beta) — locked to the CEO only */}
        {user?.email === 'josh@tnsprograms.org' && (
          <NavLink
            to="/optimizer"
            onClick={() => setMobileOpen(false)}
            data-testid="nav-optimizer"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors duration-200 mt-2 border
              ${isActive
                ? 'bg-gradient-to-r from-[#3A2F10] to-[#1A1325] text-white border-[#FFD66B] shadow-md'
                : 'bg-[#14110A] text-[#E7D9AE] border-[#3A3320] hover:bg-[#1C1810] hover:text-white'
              }`
            }
          >
            <Zap className="h-4.5 w-4.5 shrink-0 text-[#FFD66B]" />
            <span className="font-semibold tracking-wide">Optimizer</span>
            <span className="ml-auto text-[9px] font-bold uppercase bg-[#FFD66B] text-[#1A1325] rounded-full px-1.5 py-0.5">Beta</span>
          </NavLink>
        )}

        {/* CEO Life — only render for admins; gated by ceo_life_access flag */}
        {role === 'administrator' && (
          user?.ceo_life_access ? (
            <NavLink
              to="/ceo-life"
              onClick={() => setMobileOpen(false)}
              data-testid="nav-ceo-life"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors duration-200 mt-3 border
                ${isActive
                  ? 'bg-gradient-to-r from-[#1A1325] to-[#2D1B4E] text-white border-[#6B3FA0] shadow-md'
                  : 'bg-[#0F0F14] text-[#C9C2D9] border-[#2A2333] hover:bg-[#15121F] hover:text-white'
                }`
              }
            >
              <Crown className="h-4.5 w-4.5 shrink-0 text-[#FFD66B]" />
              <span className="font-semibold tracking-wide">CEO Life</span>
            </NavLink>
          ) : (
            <div
              data-testid="nav-ceo-life-locked"
              title="Restricted — request access from the CEO"
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium mt-3 border border-dashed border-white/15 text-white/30 cursor-not-allowed select-none"
            >
              <Crown className="h-4.5 w-4.5 shrink-0 opacity-40" />
              <span>CEO Life</span>
              <Lock className="h-3.5 w-3.5 ml-auto opacity-50" />
            </div>
          )
        )}

        {/* The Young CEO — solo writing studio, locked to a single user via book_access flag */}
        {user?.book_access && (
          <NavLink
            to="/book"
            onClick={() => setMobileOpen(false)}
            data-testid="nav-book-studio"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors duration-200 mt-2 border
              ${isActive
                ? 'bg-gradient-to-r from-[#FBF1D8] to-[#F5EBD0] text-[#5E3E1F] border-[#C8A45A] shadow-sm'
                : 'bg-[#FFFCF4] text-[#5C4E3F] border-[#DAD0BA] hover:bg-[#FBF6E8]'
              }`
            }
          >
            <Feather className="h-4.5 w-4.5 shrink-0 text-[#8B5A2B]" />
            <span className="font-semibold tracking-wide" style={{ fontFamily: 'Crimson Pro, Georgia, serif' }}>The Young CEO</span>
          </NavLink>
        )}
      </nav>

      <div className="p-4 border-t border-white/10">
        <div className="flex items-center gap-3 px-3 py-2.5 mb-2 rounded-xl bg-white/10">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#6B3FA0] to-[#9B7ED8] flex items-center justify-center text-white text-sm font-bold shadow-sm">
            {user?.name?.charAt(0) || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white truncate">{user?.name || 'User'}</p>
            <p className="text-xs text-white/50 truncate">
              {ROLE_LABELS[role] || 'Staff'}
              {scopeLabel && <span className="text-[10px]"> — {scopeLabel}</span>}
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          onClick={handleLogout}
          className="w-full justify-start gap-3 px-3 text-white/60 hover:text-red-300 hover:bg-white/10 transition-colors duration-200"
          data-testid="logout-button"
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </Button>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className="hidden md:flex w-64 h-screen sticky top-0 flex-col bg-[#2E1D49] border-r border-black/10"
        data-testid="sidebar"
      >
        {SidebarBody}
      </aside>

      {/* Mobile top bar */}
      <header className="md:hidden sticky top-0 z-40 bg-[#2E1D49] border-b border-black/10 flex items-center justify-between px-3 py-2 safe-top" data-testid="mobile-topbar">
        <button
          onClick={() => setMobileOpen(true)}
          aria-label="Open menu"
          className="p-2 rounded-lg hover:bg-white/10 text-white"
          data-testid="mobile-menu-btn"
        >
          <Menu className="h-5 w-5" />
        </button>
        <span className="bg-white rounded-lg px-2 py-1"><img src="/tns-logo.png" alt="TNSP" className="h-6 w-auto object-contain" /></span>
        <NavLink to="/notifications" className="p-2 rounded-lg hover:bg-white/10 text-white relative" data-testid="mobile-bell">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 bg-[#E07A5F] text-white text-[10px] min-w-4 h-4 px-1 rounded-full flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </NavLink>
      </header>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 z-50 bg-black/50"
            onClick={() => setMobileOpen(false)}
            data-testid="mobile-drawer-backdrop"
          />
          <aside
            className="md:hidden fixed left-0 top-0 z-50 w-72 max-w-[85vw] h-screen flex flex-col bg-[#2E1D49] border-r border-black/10 shadow-xl"
            data-testid="mobile-drawer"
          >
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute right-2 top-2 p-2 rounded-lg hover:bg-white/10 text-white z-10"
              aria-label="Close menu"
              data-testid="mobile-drawer-close"
            >
              <X className="h-5 w-5" />
            </button>
            {SidebarBody}
          </aside>
        </>
      )}

      {/* Mobile bottom nav for staff */}
      {role === 'staff' && (
        <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-[#EAE5F0] flex items-center justify-around safe-bottom shadow-[0_-1px_8px_rgba(45,27,78,0.06)]" data-testid="mobile-bottom-nav">
          {staffBottomLinks.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`bottom-nav-${label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-0.5 flex-1 py-2 transition-all relative ${
                  isActive ? 'text-[#6B3FA0]' : 'text-[#8E85A0]'
                }`
              }
            >
              <Icon className="h-5 w-5" />
              <span className="text-[10px] font-semibold">{label}</span>
              {label === 'Alerts' && unreadCount > 0 && (
                <span className="absolute top-1 right-[20%] bg-[#E07A5F] text-white text-[9px] min-w-3.5 h-3.5 px-1 rounded-full flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
      )}
    </>
  );
}
